// India in cut paper (public/site/paper/india.json, from scripts/build-paper-maps.mjs): the sea floor in three blues,
// the neighbours as one flat sheet, then India as ten sheets, one per height, from the plains up to the high Himalaya,
// coloured from dusk violet to the gold of the last light on the peaks. As you scroll the sheets drop onto the table
// one by one, the map tips up and the sheets lift apart, the cities rise on their pins, and then it dives into
// Chandigarh at the foot of the Shivaliks. The cities come from brand.ts; the outline is India's official one.
import { useEffect, useMemo, useRef, useState } from 'react';
import { CITIES, type City } from '../brand';
import { REDUCE, ease, lerp, loadJson, span, useScene } from './scenes';

interface IndiaData {
  w: number;
  h: number;
  frame: { lon0: number; lat0: number; kx: number; k: number };
  sea: { t: number; d: string }[];
  neighbours: string;
  land: { t: number; d: string }[];
}

/** the plains to the peaks in sandstone: dark earth, terracotta, ochre, the gold of the last light, the snow */
const LAND = ['#2e1f17', '#45281b', '#5f3220', '#7d3e25', '#9d4f2b', '#bd6b36', '#d68e48', '#e9b465', '#f6d89c', '#fff3d8'];
const SEA = ['#1b1612', '#221b16', '#2b221b'];
/** which side of its pin a coming-soon city's name sits (the north is crowded: Chandigarh, Shimla, Amritsar, Delhi) */
const SIDE: Record<string, 'l' | 'r'> = { amritsar: 'l', shimla: 'r', delhi: 'r', jaipur: 'l', mumbai: 'l', goa: 'l', bengaluru: 'l', chennai: 'r', hyderabad: 'r', kolkata: 'r' };

const STEPS = [
  {
    kicker: '01 · Bharat',
    title: (
      <>
        All of India,
        <br />
        <em>one world at a time.</em>
      </>
    ),
    text: 'We’re rebuilding India’s cities street by street, as open worlds you can walk, ride and fly through together. In your browser, with nothing to install.',
  },
  {
    kicker: '01 · The drawing board',
    title: (
      <>
        {CITIES.filter((c) => c.status !== 'live').length} more cities
        <br />
        <em>on the drawing board.</em>
      </>
    ),
    text: 'Delhi, Mumbai, Bengaluru, Kolkata and more: each will be a world of its own. Tell us where you’re from when you claim your name, and it helps us choose the next one.',
  },
  {
    kicker: '01 · First stop',
    title: (
      <>
        One is ready.
        <br />
        <em>Chandigarh.</em>
      </>
    ),
    text: 'Le Corbusier’s planned city at the foot of the Shivaliks, rebuilt at 1:1. In a closed beta now: the waitlist is open.',
  },
];

export function PaperIndia({ onChandigarh }: { onChandigarh: () => void }) {
  const sec = useRef<HTMLElement>(null);
  const world = useRef<HTMLDivElement>(null);
  const sheets = useRef<(HTMLDivElement | null)[]>([]);
  const pinRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const [data, setData] = useState<IndiaData | null>(null);
  const [step, setStep] = useState(0);
  const [pick, setPick] = useState<City | null>(null);
  const stepRef = useRef(0);

  useEffect(() => {
    let off = false;
    loadJson<IndiaData>('/site/paper/india.json')
      .then((d) => !off && setData(d))
      .catch(() => {});
    return () => {
      off = true;
    };
  }, []);

  /** where each city sits on the map (a fraction of its width and height), and on which sheet (its height) */
  const spots = useMemo(() => {
    if (!data) return [];
    const { lon0, lat0, kx, k } = data.frame;
    return CITIES.map((c) => ({
      c,
      x: ((c.lon - lon0) * kx) / data.w,
      y: ((lat0 - c.lat) * k) / data.h,
      sheet: Math.max(0, data.land.filter((l) => l.t <= c.elev).length - 1),
    }));
  }, [data]);
  const chd = spots.find((s) => s.c.status === 'live');

  useScene(sec, (q) => {
    const w = world.current;
    if (!w || !data) return;
    // still, it holds the assembled map with its pins up
    const p = REDUCE ? 0.68 : q;
    const n = Math.max(0, Math.min(2, Math.floor(p < 0.34 ? 0 : p < 0.64 ? 1 : 2)));
    if (n !== stepRef.current) {
      stepRef.current = n;
      setStep(n);
    }
    const mw = w.offsetWidth;
    const mh = w.offsetHeight;
    const stage = w.parentElement!;
    const sw = stage.clientWidth;
    const sh = stage.clientHeight;
    const open = ease.inOut(span(p, 0.24, 0.5));
    const zoom = ease.inOut(span(p, 0.7, 1));
    const tilt = lerp(lerp(26, 52, open), 18, zoom);
    const spin = lerp(lerp(-6, -14, open), -4, zoom);
    const lift = lerp(lerp(2.5, 10, open), 3, zoom);
    const scale = lerp(1, 5.2, ease.in(span(p, 0.7, 1)) * 0.35 + zoom * 0.65);
    // dive towards Chandigarh: bring it to the middle of the stage as it grows
    const cx = (chd?.x ?? 0.36) * mw;
    const cy = (chd?.y ?? 0.27) * mh;
    const dx = (sw / 2 - (w.offsetLeft + cx)) * zoom;
    const dy = (sh / 2 - (w.offsetTop + cy)) * zoom;
    w.style.transformOrigin = `${cx.toFixed(1)}px ${cy.toFixed(1)}px`;
    w.style.transform = `translate3d(${dx.toFixed(1)}px, ${dy.toFixed(1)}px, 0) rotateX(${tilt.toFixed(2)}deg) rotateZ(${spin.toFixed(2)}deg) scale(${scale.toFixed(4)})`;
    w.style.setProperty('--tilt', `${tilt.toFixed(2)}deg`);
    w.style.setProperty('--spin', `${spin.toFixed(2)}deg`);
    // the sheets drop in one after another, then stack with a gap that grows as the map tips up
    sheets.current.forEach((el, i) => {
      if (!el) return;
      const e = ease.land(span(p, 0.02 + i * 0.019, 0.12 + i * 0.019));
      el.style.transform = `translate3d(0, 0, ${((i + 1) * lift + (1 - e) * 360).toFixed(1)}px)`;
      el.style.opacity = Math.min(1, e * 2.2).toFixed(3);
    });
    pinRefs.current.forEach((el, i) => {
      const s = spots[i];
      if (!el || !s) return;
      const live = s.c.status === 'live';
      // each pin stands on the sheet for its city's height: Shimla up in the hills, Mumbai at the sea
      const z = (s.sheet + 1) * lift + 1;
      const rise = ease.out(span(p, 0.4 + i * 0.012, 0.5 + i * 0.012));
      const fade = live ? 1 - span(p, 0.9, 0.99) : 1 - span(p, 0.7, 0.8);
      el.style.transform = `translate3d(0, 0, ${z.toFixed(1)}px) rotateZ(${(-spin).toFixed(2)}deg) rotateX(${(-tilt).toFixed(2)}deg) scale(${(rise * (1 + zoom * (live ? -0.7 : 0))).toFixed(3)})`;
      el.style.opacity = (Math.min(1, rise * 1.4) * fade).toFixed(3);
      el.style.pointerEvents = rise > 0.9 && fade > 0.5 ? '' : 'none';
    });
    stage.style.setProperty('--zoom', zoom.toFixed(3));
    stage.style.setProperty('--out', span(p, 0.9, 1).toFixed(3));
  });

  const sheetsOrder = data ? data.land : [];
  return (
    <section className="pi" id="india" ref={sec} aria-labelledby="pi-title">
      <div className="pi-stage">
        <div className="pi-copy" data-step={step}>
          {STEPS.map((s, i) => (
            <div key={i} className={`pi-step${i === step ? ' on' : ''}`} aria-hidden={i !== step}>
              <p className="gx-kicker mono">{s.kicker}</p>
              <h2 id={i === 0 ? 'pi-title' : undefined}>{s.title}</h2>
              <p className="dim">{s.text}</p>
              {i === 2 && (
                <button type="button" className="gx-btn" onClick={onChandigarh} tabIndex={i === step ? 0 : -1}>
                  See Chandigarh <span aria-hidden>→</span>
                </button>
              )}
            </div>
          ))}
          <ol className="pi-dots" aria-hidden>
            {STEPS.map((_, i) => (
              <li key={i} className={i === step ? 'on' : i < step ? 'done' : ''} />
            ))}
          </ol>
        </div>
        <div className="pi-world" ref={world} role="img" aria-label="A paper-cut relief map of India, as India officially shows it, with Chandigarh live and other cities coming soon">
          {data && (
            <>
              <svg className="pi-sea" viewBox={`0 0 ${data.w} ${data.h}`} preserveAspectRatio="none" aria-hidden>
                <defs>
                  <filter id="pi-sh" x="-5%" y="-5%" width="110%" height="110%">
                    <feDropShadow dx="0" dy="2.2" stdDeviation="2.4" floodColor="#030201" floodOpacity="0.75" />
                  </filter>
                  <radialGradient id="pi-fade" cx="52%" cy="50%" r="52%">
                    <stop offset="0.5" stopColor="#fff" />
                    <stop offset="1" stopColor="#000" />
                  </radialGradient>
                  <mask id="pi-mask">
                    <rect width={data.w} height={data.h} fill="url(#pi-fade)" />
                  </mask>
                </defs>
                <g mask="url(#pi-mask)">
                  {data.sea.map((l, i) => (
                    <path key={l.t} d={l.d} fill={SEA[i]} fillRule="evenodd" filter="url(#pi-sh)" />
                  ))}
                  <path d={data.neighbours} fill="#211a15" fillRule="evenodd" filter="url(#pi-sh)" className="nb" />
                </g>
              </svg>
              {sheetsOrder.map((l, i) => (
                <div key={l.t} className="pi-sheet" ref={(el) => void (sheets.current[i] = el)} style={{ '--c': LAND[i] } as React.CSSProperties}>
                  <svg viewBox={`0 0 ${data.w} ${data.h}`} preserveAspectRatio="none" aria-hidden>
                    <path d={l.d} fillRule="evenodd" />
                  </svg>
                </div>
              ))}
              <div className="pi-pins">
                {spots.map(({ c, x, y }, i) => (
                  <button
                    key={c.id}
                    type="button"
                    ref={(el) => void (pinRefs.current[i] = el)}
                    className={`pi-pin ${c.status === 'live' ? 'live' : `soon ${SIDE[c.id] ?? 'r'}`}`}
                    style={{ left: `${(x * 100).toFixed(2)}%`, top: `${(y * 100).toFixed(2)}%` }}
                    onClick={() => (c.status === 'live' ? onChandigarh() : setPick(c))}
                    onMouseEnter={() => c.status !== 'live' && setPick(c)}
                    aria-label={c.status === 'live' ? `${c.name}: live now, closed beta` : `${c.name}: coming soon`}
                  >
                    <span className="flag">
                      <b>{c.name}</b>
                      {c.status === 'live' && <small className="mono">● Live · beta</small>}
                    </span>
                    <i className="stick" />
                    <i className="foot" />
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
        {pick && (
          <div className="pi-card" key={pick.id} role="status">
            <small className="mono">Coming soon</small>
            <b>{pick.name}</b>
            <span>{pick.blurb}</span>
          </div>
        )}
        <p className="pi-legend mono" aria-hidden>
          <span>
            <i style={{ background: LAND[0] }} />
            sea level
          </span>
          <span>
            <i style={{ background: LAND[4] }} />
            1,500 m
          </span>
          <span>
            <i style={{ background: LAND[9] }} />
            5,500 m
          </span>
          <span className="src">Relief: NOAA ETOPO1 · Borders: Natural Earth (India)</span>
        </p>
        <div className="pi-veil" aria-hidden />
      </div>
    </section>
  );
}
